/**
 * Types shared by the Worker and the SPA. Importing from here instead of
 * redeclaring on each side is what makes a response-shape change a typecheck
 * failure rather than a runtime surprise.
 *
 * Keep this package dependency-free and type-only where possible — it is
 * compiled into both a Workers bundle and a browser bundle.
 */

/** The signed-in owner, as established by GitHub sign-in. */
export interface Identity {
  /** GitHub login, lower-cased. */
  login: string
  /** Display name from GitHub, when the profile has one. */
  name: string | null
}

/** GET /api/health — public, answers before sign-in is configured. */
export interface HealthResponse {
  ok: true
  /** Mirrors the APP_ENV var: 'development' | 'production'. */
  env: string
  /**
   * Whether GitHub sign-in (client id, secret, allowed logins) and the
   * session/signing secrets are all present. False on a fresh deploy — every
   * protected route returns 503 until the secrets are put.
   */
  authConfigured: boolean
}

/** GET /api/me — the signed-in owner. */
export type MeResponse = Identity

/** Error body returned by every non-2xx API response. */
export interface ApiErrorBody {
  error: string
}

/** Where a document lives. */
export const SOURCES = [
  'slack',
  'gdrive',
  'claude_artifact',
  'notion',
  'granola',
  'gmail',
  'github',
  'web',
  /** Written by the memory itself: meeting briefs. */
  'memory',
  'other'
] as const
export type Source = (typeof SOURCES)[number]

/** Where a document was shared with the owner. */
export const CHANNELS = [
  'slack',
  'gdrive',
  'email',
  'notion',
  'granola',
  'claude',
  'calendar',
  /** Saved by the owner from a device: the iOS Share Sheet Shortcut or the Chrome extension. */
  'saved',
  'other'
] as const
export type Channel = (typeof CHANNELS)[number]

export const PERSON_ROLES = [
  'author',
  'owner',
  'editor',
  'participant',
  'attendee',
  'shared_by',
  'shared_with',
  'mentioned'
] as const
export type PersonRole = (typeof PERSON_ROLES)[number]

/** What a link is to the meeting it records. */
export const EVENT_ROLES = ['notes', 'transcript', 'recording', 'material', 'other'] as const
export type EventRole = (typeof EVENT_ROLES)[number]

/** A meeting several links are records of (Granola, Gemini, Notion, the deck…). */
export interface EventSummary {
  id: string
  title: string
  startsAt: string
  endsAt: string | null
  calendarId: string | null
}

/** A link's membership of an event, as carried by list rows. */
export interface EventRef {
  id: string
  role: EventRole
}

/** One link recorded for an event. */
export interface EventLink {
  id: string
  url: string
  title: string
  source: string
  kind: string | null
  role: EventRole
  /** A brief's summary, for the calendar; null on every other record. */
  summary: string | null
  hasText: boolean
  hasOriginal: boolean
}

/** An event as seen from one of its links: its role there, and every link. */
export interface LinkEvent extends EventSummary, EventRef {
  links: EventLink[]
}

/** 'calendar' — from Google Calendar; 'inferred' — made up from a record's own time. */
export type EventOrigin = 'calendar' | 'inferred'

/** Someone invited to a calendar event. */
export interface CalendarAttendee {
  personId: string
  name: string
  email: string | null
  organizer: boolean
  /** accepted | tentative | declined | needsAction */
  response: string | null
}

/** A meeting as the calendar view shows it: who was there, and what records it. */
export interface CalendarEvent extends EventSummary {
  origin: EventOrigin
  allDay: boolean
  /** The event in Google Calendar. */
  htmlLink: string | null
  recurringId: string | null
  /** The owner's answer to the invite. */
  response: string | null
  /** Everyone invited, rooms and the owner left out; `attendees` may hold fewer. */
  attendeeCount: number
  attendees: CalendarAttendee[]
  links: EventLink[]
}

/** One occasion a link reached the owner, with the link, for a day's list. */
export interface CalendarShare {
  id: string
  channel: string
  location: string | null
  locationUrl: string | null
  sharedAt: string
  context: string | null
  sharedBy: { id: string; name: string } | null
  link: LinkSummary
}

/** GET /api/calendar — meetings and shares between `from` and `to`. */
export interface CalendarRange {
  from: string
  to: string
  events: CalendarEvent[]
  shares: CalendarShare[]
}

export interface Person {
  id: string
  displayName: string
  email: string | null
  slackUserId: string | null
  notionUserId: string | null
  githubLogin: string | null
  organisation: string | null
}

/** A person as the People page lists them. */
export interface PersonSummary extends Person {
  linkCount: number
  eventCount: number
  /**
   * Where they are known from: the sources of their links, `slack` for a
   * Slack account, `calendar` for a meeting invitation.
   */
  sources: string[]
}

/** A person with everything they can be found by (GET /api/people/:id). */
export interface PersonDetail extends PersonSummary {
  itemCount: number
  /** Every identifier, merged duplicates' included. */
  identities: { kind: 'email' | 'slack' | 'notion' | 'github'; value: string }[]
  /** Other names they went by: a merged duplicate's, or one before a rename. */
  aliases: string[]
  /** Newest first, at most 8. */
  recentLinks: { id: string; title: string; source: string; lastSharedAt: string }[]
}

/** Someone in the owner's org, as the org lists them. */
export interface OrgPerson {
  id: string
  name: string
  email: string | null
}

/** One placed person's line (0018_org.sql). */
export interface OrgLine {
  person: OrgPerson
  /** Who they report to; null when it is the owner (`toOwner`) or nobody known. */
  manager: OrgPerson | null
  toOwner: boolean
  role: { id: string; name: string } | null
  /** Steps up to the owner (1: reports to him); null when the line does not reach him. */
  distance: number | null
}

/** A kind of report and who they go to: "Tech lead" → Ania. */
export interface OrgRole {
  id: string
  name: string
  managers: OrgPerson[]
  /** People placed with this role. */
  count: number
}

export interface OrgForumMember extends OrgPerson {
  /** On an invite of the series' recent meetings; null when the forum has no series. */
  invited: boolean | null
}

/** A forum's calendar series: its meetings, however Google split it. */
export interface OrgSeries {
  id: string
  /** The newest occurrence's title. */
  title: string
  lastAt: string | null
  nextAt: string | null
  /** Typical days between meetings, from the calendar; null with fewer than two. */
  cadenceDays: number | null
  ownerOrganizes: boolean
}

/** A group the owner sits in or chairs (The Hive, his management team). */
export interface OrgForum {
  id: string
  name: string
  ownerChairs: boolean
  series: OrgSeries | null
  members: OrgForumMember[]
  /** On the series' recent invites, not members: whom to add, or ignore. */
  invitedNotMembers: OrgPerson[]
  /** One-off meetings added to it by hand (an off-site). */
  events: { id: string; title: string; startsAt: string }[]
}

/** Someone the owner deals with who has no place in his org yet. */
export interface OrgSuggestion {
  person: OrgPerson
  reasons: string[]
}

/** The owner's org: GET /api/org, MCP org_get. */
export interface OrgView {
  /** The owner's own manager. */
  manager: OrgPerson | null
  lines: OrgLine[]
  roles: OrgRole[]
  forums: OrgForum[]
  suggestions: OrgSuggestion[]
}

/** A recurring series a forum can be matched to (GET /api/org/series). */
export interface OrgSeriesCandidate extends OrgSeries {
  /** Meetings of it the memory knows. */
  occurrences: number
  attendeeCount: number
  /** Already the series of this forum. */
  forumId: string | null
}

/** One reason two people may be one human. */
export interface PersonEvidence {
  kind:
    | 'shared_identifier'
    | 'same_name'
    | 'email_matches_name'
    | 'same_address_elsewhere'
    | 'name_extends'
    | 'similar_spelling'
    | 'first_name'
    | 'together'
  text: string
}

/** A possible duplicate, for the owner to merge or keep apart. Never merged automatically. */
export interface PersonCandidate {
  /** The suggested survivor: more identifiers, links and a real name. */
  keep: PersonDetail
  /** The suggested duplicate, folded into `keep` on merge. */
  fold: PersonDetail
  score: number
  evidence: PersonEvidence[]
}

/** GET /api/people/review */
export interface PersonReview {
  total: number
  candidates: PersonCandidate[]
}

export interface LinkPerson {
  person: Person
  roles: PersonRole[]
}

export interface Share {
  id: string
  channel: string
  location: string | null
  locationUrl: string | null
  externalId: string | null
  sharedBy: Person | null
  sharedAt: string
  context: string | null
}

export interface DocumentCopy {
  id: string
  kind: 'text' | 'original'
  mime: string
  filename: string | null
  bytes: number
  sha256: string
  sourceRev: string | null
  capturedAt: string
  status: 'pending' | 'ready'
}

/** A link as listed: enough to recognise it, not everything. */
export interface LinkSummary {
  id: string
  url: string
  source: string
  externalId: string | null
  title: string
  kind: string | null
  summary: string | null
  tags: string[]
  starred: boolean
  archived: boolean
  firstSharedAt: string | null
  lastSharedAt: string | null
  updatedAt: string
  /** Display names, roles collapsed. */
  people: string[]
  hasText: boolean
  hasOriginal: boolean
  /** The owner's own note (kind 'todo'): the kind of the item it records, a to-do or a decision. */
  itemKind?: ItemKind
  /** The meetings this link is a record of; the list folds a meeting's records together. */
  events: EventRef[]
}

export interface LinkDetail extends LinkSummary {
  notes: string | null
  summaryBy: string | null
  summaryAt: string | null
  docCreatedAt: string | null
  docUpdatedAt: string | null
  createdAt: string
  /** In the Trash since; null for a live link. */
  trashedAt: string | null
  shares: Share[]
  linkPeople: LinkPerson[]
  documents: DocumentCopy[]
  events: LinkEvent[]
}

/** GET /api/tags — a tag and how many non-archived links carry it. */
export interface TagCount {
  tag: string
  count: number
}

/** GET /api/search — ⌘K: a few of each, best match first. */
export interface SearchResult {
  links: LinkSummary[]
  /** Any status, not trashed. */
  items: LedgerItem[]
  people: PersonSummary[]
}

export interface LinkSearchResult {
  items: LinkSummary[]
  /** Opaque; pass back to get the next page. Null when there is none. */
  nextCursor: string | null
}

export interface SyncSourceState {
  source: string
  syncedUntil: string
  cursor: string | null
  lastRunId: string | null
  lastSuccessAt: string | null
  updatedAt: string
}

/** The scheduled tasks that record their runs: the sync, then the ledger extraction and the evening briefs. */
export const RUN_TASKS = ['sync', 'ledger', 'briefs'] as const
export type RunTask = (typeof RUN_TASKS)[number]

export interface SyncRun {
  id: string
  task: RunTask
  startedAt: string
  finishedAt: string | null
  status: 'running' | 'ok' | 'partial' | 'failed'
  agent: string | null
  stats: Record<string, unknown> | null
  /** What went wrong, or for the sync what needs the owner's action. Markdown. */
  error: string | null
  /** The run's digest. Markdown. */
  report: string | null
  /** The server's check of each step, written when the run completed. */
  checks: RunChecks | null
}

/**
 * How the server knows a step was done: from its own data (`outcome`), from a
 * memory tool called during the run (`call`), or only from the run's own
 * count (`account`), for work it cannot see.
 */
export type StepCheck = 'outcome' | 'call' | 'account'

export interface RunStepCheck {
  id: string
  check: StepCheck
  /** A failed enforced step refuses the run; an unenforced one is only noted. */
  enforce: boolean
  state: 'done' | 'skipped' | 'failed'
  count?: number
  /** Why it was skipped, or what is missing. */
  note?: string
}

export interface RunChecks {
  steps: RunStepCheck[]
}

export interface SyncStatus {
  sources: SyncSourceState[]
  /** Newest first, every task mixed. */
  recentRuns: SyncRun[]
}

export interface Stats {
  links: number
  /** Starred and not archived — what the Starred filter shows. */
  starred: number
  archived: number
  /** In the Trash (not yet deleted for good). */
  trashed: number
  /** People on at least one link or ledger item. */
  people: number
  /** Every person row, calendar-only invitees included. */
  peopleTotal: number
  shares: number
  documents: number
  events: number
  bySource: Record<string, number>
  /** Source 'memory' split by what each link is; a kind with no links is left out. */
  byOwn: Partial<Record<OwnKind, number>>
}

/**
 * The memory's own links the library lists, by what they are: a meeting
 * brief and a debrief. `GET /api/links` filters on one with `own`. The
 * owner's note behind a to-do or a decision he typed is not one: it is the
 * ledger's, and the library never lists it (worker db/links.ts, NOTE).
 */
export const OWN_KINDS = ['brief', 'debrief'] as const
export type OwnKind = (typeof OWN_KINDS)[number]

/** A device token for POST /share. The token itself is only ever shown once. */
export interface ShareToken {
  id: string
  label: string
  createdAt: string
  lastUsedAt: string | null
}

/** POST /api/share-tokens — the one response that carries the token. */
export interface ShareTokenCreated extends ShareToken {
  token: string
}

/**
 * What a refetch did with a link: `stored` a new text copy, found the page
 * `unchanged`, `kept` a copy captured signed in (refetch with force to replace
 * it), or could not read it. Only open-web links are refetched (`not_web`).
 */
export type RefetchOutcome =
  | 'stored'
  | 'unchanged'
  | 'kept'
  | 'unreadable'
  | 'not_web'
  | 'not_found'

export interface RefetchResult {
  id: string
  outcome: RefetchOutcome
  /** True when the link went back in the inbox for the next sync to summarise. */
  queued?: boolean
}

/** What a backfill walks: web links with no text copy, or only the server's own fetch. */
export type RefetchScope = 'missing' | 'fetched'

/** POST /api/refetch and the link_refetch tool's backfill: one batch. */
export interface RefetchBatch {
  results: RefetchResult[]
  /** Pass as `after` for the next batch; null when this was the last. */
  next: string | null
  /** Web links without a text copy still ahead of the cursor. */
  remaining: number
}

/** POST /share — what the Shortcut shows back. */
export interface ShareResponse {
  id: string
  title: string
  /** False when the link was already in the memory. */
  created: boolean
  /** True when the save's page text was stored as the link's text copy. */
  textStored: boolean
  /** The link in the web UI. */
  url: string
  /** One line for the Shortcut's notification, e.g. "Saved: Plan". */
  message: string
}

/**
 * POST /share when nothing was saved. Shortcuts hands an error body on like
 * any other, so it carries the same `message` field the notification shows.
 */
export interface ShareError {
  error: 'unauthenticated' | 'auth_not_configured' | 'no_url'
  message: string
}

/**
 * The ledger (0007_ledger.sql). i_owe: the owner's to do. owed_to_me:
 * someone's to do for him. decision: settled, nothing to do — it has a date,
 * not a due date, and is archived (status done) rather than closed.
 * follow_up: someone else's to do in a meeting he runs.
 */
export const ITEM_KINDS = ['i_owe', 'owed_to_me', 'decision', 'follow_up'] as const
export type ItemKind = (typeof ITEM_KINDS)[number]

export const ITEM_STATUSES = ['open', 'done', 'dropped', 'stale'] as const
export type ItemStatus = (typeof ITEM_STATUSES)[number]

/**
 * How a suggested decision bears on an open to-do: it settles it, settles it
 * partly (a step is left), or makes it moot (dropped, not done).
 */
export const SUGGESTION_FITS = ['settles', 'partly', 'moot'] as const
export type SuggestionFit = (typeof SUGGESTION_FITS)[number]

/** One commitment or decision, with the link it was read in. */
export interface LedgerItem {
  id: string
  kind: ItemKind
  text: string
  /** Verbatim from the source's text copy. */
  quote: string
  status: ItemStatus
  /** The counterpart: who owes it, or who it is owed to. */
  person: { id: string; name: string } | null
  /**
   * Everyone else it is with, by name. Only on the owner's own to-dos and
   * decisions (link kind 'todo'): the people he named beside the counterpart,
   * and any the sync read off his screenshot. Empty on an extracted item,
   * whose link's people are the whole meeting's.
   */
  others: { id: string; name: string }[]
  /**
   * `date`: the day the link happened (YYYY-MM-DD, owner's zone). `kind`
   * 'todo': the owner's own to-do, its source the note he typed.
   */
  link: { id: string; title: string; source: string; kind: string | null; date: string | null }
  eventId: string | null
  /**
   * YYYY-MM-DD. A to-do's deadline. On a decision, the stored date it
   * names (when it takes effect) — never a deadline; read `date` instead.
   */
  dueDate: string | null
  /** The deadline as written: "before Monday". On a decision, its date as written. */
  dueHint: string | null
  /** A decision's date: the one it names, else the day it was made. Null on a to-do. */
  date: string | null
  closedBy: { id: string; title: string } | null
  closedNote: string | null
  /** A to-do settled by a decision (done), or made moot by one (dropped): that decision. */
  decidedBy: { id: string; text: string } | null
  /** On a decision: the to-dos it settled. Empty on a to-do. */
  settled: { id: string; text: string }[]
  /**
   * An open to-do the ledger task thinks a standing decision settles, partly
   * settles or makes moot: never closed for it, only offered to the owner,
   * who accepts (done, decided by it; dropped when moot) or dismisses it.
   * `note`: why, and on a partial fit what is left.
   */
  suggestedDecision: {
    id: string
    text: string
    fit: SuggestionFit
    note: string | null
    at: string
  } | null
  /** Trashed as the same commitment as another item: the one it was merged into. */
  mergedInto: { id: string; text: string } | null
  closedAt: string | null
  /** Set once the owner changed it by hand; extraction no longer rewrites it. */
  editedAt: string | null
  /** In the ledger's Trash: not a real item, never recorded again. */
  trashedAt: string | null
  lastSeenAt: string
  /**
   * An open follow-up or delegation nobody has mentioned in two meetings of
   * its series (or 21 days outside one): worth raising, never closed for it.
   */
  quiet: boolean
  createdAt: string
  updatedAt: string
}

/** PATCH /api/items/:id — the owner's own changes. */
export interface ItemPatch {
  kind?: ItemKind
  text?: string
  /** Re-assign to another person, or null for nobody. */
  personId?: string | null
  dueDate?: string | null
  dueHint?: string | null
  /** Close (done, dropped, stale) or reopen (open). */
  status?: ItemStatus
  /** With status 'done' or 'dropped': the standing decision that settled it, or made it moot. */
  decidedById?: string | null
  note?: string | null
  /** No to the ledger task's suggested decision: never offered again. */
  dismissSuggestion?: boolean
  trashed?: boolean
}

/** Someone below a 1:1's attendee in the owner's org, and what they hold. */
export interface BriefAreaHolder {
  person: { id: string; name: string }
  /** Their own manager, when it is not the attendee: "Olaf, via Frank". */
  via: { id: string; name: string } | null
  items: BriefItem[]
}

/** A ledger item as a brief lists it. */
export interface BriefItem {
  id: string
  kind: ItemKind
  text: string
  status: ItemStatus
  person: { id: string; name: string } | null
  dueDate: string | null
  dueHint: string | null
  /** A decision's date (it has no due date): the one it names, else the day it was made. */
  date: string | null
  link: { id: string; title: string }
  /** The owner's own version: it outranks the notes. */
  edited: boolean
  /** Nobody has mentioned it in two meetings of its series: raise it. */
  quiet: boolean
  closedNote: string | null
  updatedAt: string
}

/** A link involving the brief's people, for "what changed since". */
export interface BriefLink {
  id: string
  title: string
  source: string
  kind: string | null
  date: string | null
  summary: string | null
}

/** A meeting the owner and a 1:1's attendee were both in. */
export interface BriefSharedMeeting {
  id: string
  title: string
  startsAt: string
  notes: { id: string; title: string }[]
  decisions: BriefItem[]
}

/** A record of the meeting or an earlier occurrence of its series. */
export interface BriefMaterial extends BriefLink {
  /** How it belongs to its meeting: notes, transcript, material, other. */
  role: string
  meeting: { id: string; startsAt: string }
}

/**
 * The facts a meeting brief is written from (brief_for_event /
 * brief_for_person). Assembled live from the ledger, so the owner's edits
 * and trash are always in it.
 */
export interface BriefFacts {
  event: {
    id: string
    title: string
    startsAt: string
    endsAt: string | null
    recurring: boolean
    ownerOrganizes: boolean
    attendeeCount: number
  } | null
  attendees: { id: string; name: string; email: string | null; organizer: boolean }[]
  previous: {
    id: string
    title: string
    startsAt: string
    notes: { id: string; title: string; summary: string | null }[]
  } | null
  /** Since the previous meeting, else the last 30 days. */
  since: string
  /**
   * In a 1:1, every open item with its attendee or in its series. In a group
   * meeting, only its series' own; follow-ups only when the owner runs it.
   */
  open: { iOwe: BriefItem[]; owedToMe: BriefItem[]; followUp: BriefItem[]; decisions: BriefItem[] }
  /**
   * In a group meeting: the attendees' open to-dos made elsewhere (threads,
   * other group meetings) — never in a 1:1, which is where those get raised.
   * For the brief to use at most three, where plainly about the meeting's
   * subject. Empty for a 1:1.
   */
  candidates: BriefItem[]
  /** Closed since `since`. In a group meeting, never an item made in a 1:1. */
  closedSince: BriefItem[]
  /**
   * Links since `since` involving the attendees — in a group meeting, at
   * least two of them, so the topic shows and one person's traffic does not.
   */
  recent: BriefLink[]
  /**
   * Records of this meeting (an agenda, a pre-read) and of its series'
   * earlier occurrences in the last 90 days, newest first; `previous.notes`
   * and briefs left out.
   */
  material: BriefMaterial[]
  /**
   * Decisions dated since `since`, made in the series or read out of a
   * `recent` or `material` link, whatever their status. An open one is also
   * in `open.decisions`.
   */
  decidedSince: BriefItem[]
  /**
   * In a 1:1: the other meetings since `since` that its one attendee was in
   * too, with their notes and the decisions read there — what to pick up
   * between the two of them. Empty for a group meeting.
   */
  alsoMet: BriefSharedMeeting[]
  /**
   * In a 1:1 with someone who has people below them in the owner's org: what
   * those people hold (follow-ups, and what the owner delegated to them), at
   * any depth, quiet and overdue first. Empty otherwise.
   */
  area: BriefAreaHolder[]
  /** The brief already stored for this event, if any. */
  brief: { linkId: string; writtenAt: string } | null
}

/**
 * The Ledger page's views. `all`: every open to-do (any kind but a
 * decision), soonest due first. `waiting`: owed to the owner; `follow_up`:
 * his follow-ups. The page groups the to-do views by when they are due.
 */
export const LEDGER_VIEWS = [
  'all',
  'i_owe',
  'waiting',
  'follow_up',
  'decisions',
  'closed',
  'trash'
] as const
export type LedgerView = (typeof LEDGER_VIEWS)[number]

/**
 * GET /api/items/counts — how many items each view holds (all people), and
 * how many of the open to-dos are overdue.
 */
export type LedgerCounts = Record<LedgerView, number> & { overdue: number }

/** GET /api/items */
export interface LedgerList {
  /** YYYY-MM-DD in the owner's time zone: what "overdue" is counted from. */
  today: string
  items: LedgerItem[]
  /** The next planned 1:1 with each of the items' people who has one. */
  nextOneToOne: Record<string, NextOneToOne>
}

/** A planned 1:1: a meeting with one invitee besides the owner. */
export interface NextOneToOne {
  id: string
  title: string
  startsAt: string
}

/** GET /api/events/:id/brief — the stored brief, and the ledger as it is now. */
export interface EventBrief {
  brief: { linkId: string; writtenAt: string; text: string } | null
  open: BriefFacts['open']
  closedSince: BriefItem[]
  /** An item in it was edited, closed or added after the brief was written. */
  changedSince: boolean
}

/**
 * What came of one point of a meeting, in a debrief written after it (a
 * walking meeting, a call nobody recorded). `they_will`: someone else does
 * it (`personId`); `i_will`: the owner does.
 */
export const DEBRIEF_OUTCOMES = [
  'decided',
  'they_will',
  'i_will',
  'discussed',
  'parked',
  'not_discussed'
] as const
export type DebriefOutcome = (typeof DEBRIEF_OUTCOMES)[number]

export interface DebriefPoint {
  title: string
  /** What the brief said about it: context, not the owner's record. */
  detail: string | null
  /** Null: not recorded, left out of the debrief's text. */
  outcome: DebriefOutcome | null
  /** The owner's own words on what came of it. */
  note: string
  /** Who will do it, for `they_will`. */
  personId: string | null
  dueDate: string | null
}

export interface DebriefInput {
  points: DebriefPoint[]
  /** Anything else that came up. */
  notes: string
}

/** GET /api/events/:id/debrief — the meeting, its brief's agenda, and the stored debrief. */
export interface EventDebrief {
  event: {
    id: string
    title: string
    startsAt: string
    endsAt: string | null
    attendees: { id: string; name: string }[]
  }
  /** The brief's Open and Agenda points: what a new debrief starts from. */
  agenda: { title: string; detail: string | null }[]
  briefLinkId: string | null
  debrief: (DebriefInput & { linkId: string; writtenAt: string }) | null
}
